import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useCheckout from '@jwp/ott-hooks-react/src/useCheckout';
import { modalURLFromLocation, modalURLFromWindowLocation } from '@jwp/ott-ui-react/src/utils/location';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { FormValidationError } from '@jwp/ott-common/src/errors/FormValidationError';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { getModule } from '@jwp/ott-common/src/modules/container';

import CheckoutForm from '../../../components/CheckoutForm/CheckoutForm';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import PayPal from '../../../components/PayPal/PayPal';
import NoPaymentRequired from '../../../components/NoPaymentRequired/NoPaymentRequired';
import PaymentForm, { type PaymentFormData } from '../../../components/PaymentForm/PaymentForm';
import AdyenInitialPayment from '../../AdyenInitialPayment/AdyenInitialPayment';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adyenUpdating, setAdyenUpdating] = useState(false); // @todo: integrate AdyenInitialPayment into useCheckout

  const [couponFormOpen, setCouponFormOpen] = useState(false);
  const [showCouponCodeSuccess, setShowCouponCodeSuccess] = useState(false);
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();

  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user.id : 'Guest user';
  const deviceType = true ? useGADeviceType() : null;
  const { isGoogleAnalytics } = useAppConfig();
  const { subscription } = useAccountStore.getState();
  const accountController = getModule(AccountController);

  const chooseOfferUrl = modalURLFromLocation(location, 'choose-offer');
  const welcomeUrl = modalURLFromLocation(location, 'welcome');
  const closeModalUrl = modalURLFromLocation(location, null);

  const backButtonClickHandler = () => navigate(chooseOfferUrl);

  const trackPaymentInitiateEvent = (asset_id: string, price: string, currency: string, method: string, provider: string | null) => {
    sendGAEvent('payment_initiated', {
      asset_id,
      price,
      currency,
      method,
      provider,
      userid_no: userId,
      device_type: deviceType,
    });
  };

  const trackPaymentSuccessEvent = (asset_id: string, price: string, currency: string, transaction_id: string) => {
    sendGAEvent('payment_success', {
      asset_id,
      price,
      currency,
      transaction_id,
      userid_no: userId,
      device_type: deviceType,
    });
  };

  const trackPaymentFailureEvent = (asset_id: string, error_message: string, payment_method: string, provider: string) => {
    sendGAEvent('payment_failure', {
      asset_id,
      error_message,
      payment_method,
      provider,
      userid_no: userId,
      device_type: deviceType,
    });
  };

  const { selectedOffer, offerType, paymentMethods, order, isSubmitting, updateOrder, submitPaymentWithoutDetails, submitPaymentPaypal, submitPaymentStripe } =
    useCheckout({
      onUpdateOrderSuccess: () => !!couponCode && setShowCouponCodeSuccess(true),
      onSubmitPaymentWithoutDetailsSuccess: () => {
        trackPaymentSuccessEvent(selectedOffer?.offerId || '', order?.totalPrice?.toString() || '', order?.currency || '', order?.id?.toString() || '');
        announce(t('checkout.payment_success'), 'success');

        return navigate(offerType === 'svod' ? welcomeUrl : closeModalUrl, { replace: true });
      },
      onSubmitPaypalPaymentSuccess: ({ redirectUrl }) => {
        trackPaymentSuccessEvent(selectedOffer?.offerId || '', order?.totalPrice?.toString() || '', order?.currency || '', order?.id?.toString() || '');
        window.location.href = redirectUrl;
      },
      onSubmitStripePaymentSuccess: async () => {
        const respone = await accountController.reloadSubscriptions();
        trackPaymentSuccessEvent(selectedOffer?.offerId || '', order?.totalPrice?.toString() || '', order?.currency || '', order?.id?.toString() || '');
        navigate(modalURLFromLocation(location, 'waiting-for-payment', { offerId: selectedOffer?.offerId }), { replace: true });
      },
      onSubmitPaymentWithoutDetailsFailure: (error: any) => {
        trackPaymentFailureEvent(selectedOffer?.offerId || '', error, paymentMethod?.methodName || 'card', paymentMethod?.provider || 'stripe');
      },
      onSubmitPaypalPaymentFailure: (error: any) => {
        trackPaymentFailureEvent(selectedOffer?.offerId || '', error, paymentMethod?.methodName || 'paypal', paymentMethod?.provider || 'paypal');
      },
      onSubmitStripePaymentFailure: (error: any) => {
        trackPaymentFailureEvent(selectedOffer?.offerId || '', error.message, paymentMethod?.methodName || 'card', paymentMethod?.provider || 'stripe');
        navigate(modalURLFromLocation(location, 'payment-error'));
      },
    });

  const {
    values: { couponCode, paymentMethodId },
    setValue,
    submitting: couponFormSubmitting,
    errors,
    handleChange,
    handleSubmit,
  } = useForm({
    initialValues: { couponCode: '', paymentMethodId: paymentMethods?.[0]?.id?.toString() || '' },
    onSubmit: async ({ couponCode, paymentMethodId }) => {
      setShowCouponCodeSuccess(false);

      return await updateOrder.mutateAsync({ couponCode, paymentMethodId: parseInt(paymentMethodId) });
    },
    onSubmitSuccess: ({ couponCode }): void => setShowCouponCodeSuccess(!!couponCode),
    onSubmitError: ({ error }) => {
      if (error instanceof FormValidationError && error.errors.order?.includes(`Order with id ${order?.id} not found`)) {
        navigate(modalURLFromLocation(location, 'choose-offer'), { replace: true });
      }
    },
  });

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);

    // Always send payment method to backend
    updateOrder.mutateAsync({ couponCode, paymentMethodId: parseInt(event.target.value) });
  };

  useEffect(() => {
    if (!selectedOffer) {
      return navigate(chooseOfferUrl, { replace: true });
    }
  }, [navigate, chooseOfferUrl, selectedOffer]);

  // Pre-select first payment method
  useEffect(() => {
    if (!paymentMethods?.length) { return; }

    setValue('paymentMethodId', paymentMethods[0].id.toString());
  }, [paymentMethods, setValue]);

  // clear after closing the checkout modal
  useEffect(() => {
    return () => setShowCouponCodeSuccess(false);
  }, []);

  useEffect(() => {
    if (!selectedOffer || !paymentMethods) {
      return;
    }
    const paymentMethod = paymentMethods?.find((method) => method.id === parseInt(paymentMethodId));
    sendGAEvent('paywall_view', {
      asset_id: selectedOffer?.offerId,
      title: selectedOffer?.offerTitle,
      price: selectedOffer?.offerPrice,
      currency: selectedOffer?.offerCurrency,
      provider: paymentMethod?.provider,
      userid_no: userId,
      device_type: deviceType,
    });
  }, [selectedOffer, paymentMethods]);

  // loading state
  if (!selectedOffer || !order || !paymentMethods || !offerType) {
    return (
      <div style={{ height: 300 }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  const cancelUrl = modalURLFromWindowLocation('payment-cancelled');
  const waitingUrl = modalURLFromWindowLocation('waiting-for-payment', { offerId: selectedOffer?.offerId });
  const errorUrl = modalURLFromWindowLocation('payment-error');
  const successUrlPaypal = offerType === 'svod' ? waitingUrl : closeModalUrl;
  const referrer = window.location.href;

  const paymentMethod = paymentMethods?.find((method) => method.id === parseInt(paymentMethodId));
  const noPaymentRequired = !order?.requiredPaymentDetails;
  const isStripePayment = paymentMethod?.methodName === 'card' && paymentMethod?.provider === 'stripe';
  const isAdyenPayment = paymentMethod?.methodName === 'card' && paymentMethod?.paymentGateway === 'adyen';
  const isPayPalPayment = paymentMethod?.methodName === 'paypal';
  return (
    <CheckoutForm
      order={order}
      offer={selectedOffer}
      offerType={offerType}
      onBackButtonClick={backButtonClickHandler}
      paymentMethods={paymentMethods}
      paymentMethodId={paymentMethodId}
      onPaymentMethodChange={handlePaymentMethodChange}
      onCouponFormSubmit={handleSubmit}
      onCouponInputChange={handleChange}
      onRedeemCouponButtonClick={() => setCouponFormOpen(true)}
      onCloseCouponFormClick={() => setCouponFormOpen(false)}
      couponInputValue={couponCode}
      couponFormOpen={couponFormOpen}
      couponFormApplied={showCouponCodeSuccess}
      couponFormSubmitting={couponFormSubmitting}
      couponFormError={errors.couponCode}
      submitting={isSubmitting || adyenUpdating}
    >
      {noPaymentRequired && <NoPaymentRequired onSubmit={submitPaymentWithoutDetails.mutateAsync} error={submitPaymentWithoutDetails.error?.message || null} />}
      {isStripePayment && (
        <PaymentForm
          onPaymentFormSubmit={async (cardPaymentPayload: PaymentFormData) => {
            trackPaymentInitiateEvent(
              selectedOffer.offerId,
              selectedOffer.offerPrice?.toString(),
              selectedOffer.offerCurrency,
              paymentMethod.methodName,
              paymentMethod.provider ? paymentMethod.provider : null,
            );
            await submitPaymentStripe.mutateAsync({ cardPaymentPayload, referrer, returnUrl: waitingUrl });
          }}
        />
      )}
      {isAdyenPayment && (
        <>
          <AdyenInitialPayment
            paymentSuccessUrl={offerType === 'svod' ? welcomeUrl : closeModalUrl}
            setUpdatingOrder={setAdyenUpdating}
            orderId={order.id}
            type="card"
          />
        </>
      )}
      {isPayPalPayment && (
        <PayPal
          onSubmit={() => {
            trackPaymentInitiateEvent(
              selectedOffer.offerId,
              selectedOffer.offerPrice?.toString(),
              selectedOffer.offerCurrency,
              paymentMethod.methodName,
              paymentMethod.provider ? paymentMethod.provider : null,
            );
            submitPaymentPaypal.mutate({ successUrl: successUrlPaypal, waitingUrl, cancelUrl, errorUrl, couponCode });
          }}
          error={submitPaymentPaypal.error?.message || null}
        />
      )}
    </CheckoutForm>
  );
};

export default Checkout;
